import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
    Row,
    Col,
    Form,
    Input,
    Button,
    Table,
    Tag,
    Tooltip,
    message,
} from "antd";
import {
    EditOutlined,
    SaveOutlined,
    DesktopOutlined,
    ApartmentOutlined,
    BankOutlined,
    PrinterOutlined,
    BarcodeOutlined,
    NumberOutlined,
    SearchOutlined,
    CloseOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    SettingOutlined,
    FormOutlined,
    UnorderedListOutlined,
    InfoCircleOutlined,
    ColumnWidthOutlined,
    ZoomInOutlined,
} from "@ant-design/icons";
import { CREATE_jwel, CREATE_jwel1 } from "../../Config/Config";

// API returns null for empty columns; never show / save the text "null"
const clean = (v) => (v === null || v === undefined || v === "null" ? "" : v);

// Numeric columns: blank -> SQL NULL, otherwise a plain number
const numOrNull = (v) => {
    const s = String(v ?? "").trim();
    return s !== "" && !isNaN(Number(s)) ? Number(s) : "NULL";
};

// Allow only digits with an optional decimal part (blank is allowed)
const NUMERIC_RULE = {
    pattern: /^\d*\.?\d*$/,
    message: "Numbers only",
};

const CounterConfigMaster = () => {
    const [form] = Form.useForm();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState(""); // UI only

    // UI only: which row is being edited (derived from the form's SNO field)
    const selectedSno = Form.useWatch("sno", form);
    const selectedRecord = tableData.find((r) => r.sno === selectedSno);
    const noSelection = !selectedSno; // UI only: fields disabled until a row is picked

    const tenantName = localStorage.getItem("tenantName");

    const sysnameRef = useRef(null);
    const stationRef = useRef(null);
    const branchRef = useRef(null);
    const billingRef = useRef(null);
    const estimationRef = useRef(null);
    const barcodeRef = useRef(null);
    const widthRef = useRef(null);
    const scaleRef = useRef(null);
    const updateBtnRef = useRef(null);

    useEffect(() => {
        getCounterConfig();
    }, []);

    const getCounterConfig = async () => {
        try {
            setLoading(true);

            const response = await axios.get(
                `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName`,
                {
                    params: {
                        tableName: "COUNTER_CONFIG",
                    },
                    headers: {
                        tenantName,
                    },
                }
            );

            const formattedData = response.data.map((item) => ({
                key: item.SNO,
                sno: item.SNO,
                sysname: clean(item.SYSNAME),
                station: clean(item.STATION),
                branchname: clean(item.BRANCHNAME),
                billingprinter: clean(item.BILLINGPRINTER),
                estimationprinter: clean(item.ESTIMATIONPRINTER),
                barcodeprinter: clean(item.BARCODEPRINTER),
                width: String(clean(item.WIDTH)),
                scale: String(clean(item.SCALE)),
            }));

            setTableData(formattedData);
        } catch (error) {
            message.error("Failed to load data");
        } finally {
            setLoading(false);
        }

    };

    const handleEdit = (record) => {
        form.setFieldsValue({
            sno: record.sno,
            sysname: record.sysname,
            station: record.station,
            branchname: record.branchname,
            billingprinter: record.billingprinter,
            estimationprinter: record.estimationprinter,
            barcodeprinter: record.barcodeprinter,
            width: record.width,
            scale: record.scale,
        });

        setTimeout(() => {
            sysnameRef.current?.focus();
        }, 100);
    };

    const handleSubmit = async (values) => {
        try {
            await axios.post(
                `${CREATE_jwel1}/api/Master/CommonUpdateWithWhere`,
                {
                    tableName: "COUNTER_CONFIG",
                    updateString:
                        `SYSNAME='${clean(values.sysname)}',` +
                        `STATION='${clean(values.station)}',` +
                        `BRANCHNAME='${clean(values.branchname)}',` +
                        `BILLINGPRINTER='${clean(values.billingprinter)}',` +
                        `ESTIMATIONPRINTER='${clean(values.estimationprinter)}',` +
                        `BARCODEPRINTER='${clean(values.barcodeprinter)}',` +
                        `WIDTH=${numOrNull(values.width)},` +
                        `SCALE=${numOrNull(values.scale)}`,
                    whereString: `SNO=${values.sno}`,
                },
                {
                    headers: {
                        tenantName,
                    },
                }
            );

            message.success("Record Updated Successfully");
            getCounterConfig();
            form.resetFields();
        } catch (error) {
            message.error("Update Failed");
        }

    };

    // ─── UI-only derived data ─────────────────────────────────────────────────
    const isConfigured = (r) =>
        !!(r.branchname && r.estimationprinter && r.barcodeprinter);

    const totalCount = tableData.length;
    const readyCount = tableData.filter(isConfigured).length;
    const pendingCount = totalCount - readyCount;

    const q = search.trim().toLowerCase();
    const filteredData = q
        ? tableData.filter((r) =>
            [
                r.sno,
                r.sysname,
                r.station,
                r.branchname,
                r.billingprinter,
                r.estimationprinter,
                r.barcodeprinter,
                r.width,
                r.scale,
            ]
                .join(" ")
                .toLowerCase()
                .includes(q)
        )
        : tableData;

    const notSet = <span className="cc-muted">Not set</span>;

    const columns = [
        {
            title: "SNO",
            dataIndex: "sno",
            width: 70,
            align: "center",
            render: (v) => <span className="cc-sno">{v}</span>,
        },
        {
            title: "System",
            dataIndex: "sysname",
            width: 190,
            render: (v) =>
                v ? (
                    <span className="cc-sys">
                        <DesktopOutlined className="cc-sys-icon" />
                        {v}
                    </span>
                ) : (
                    notSet
                ),
        },
        {
            title: "Station",
            dataIndex: "station",
            width: 120,
            render: (v) => (v ? <Tag className="cc-station-tag">{v}</Tag> : notSet),
        },
        {
            title: "Branch",
            dataIndex: "branchname",
            width: 130,
            render: (v) => v || notSet,
        },
        {
            title: "Billing",
            dataIndex: "billingprinter",
            width: 140,
            render: (v) => v || notSet,
        },
        {
            title: "Estimation",
            dataIndex: "estimationprinter",
            width: 140,
            render: (v) => v || notSet,
        },
        {
            title: "Barcode",
            dataIndex: "barcodeprinter",
            width: 140,
            render: (v) => v || notSet,
        },
        {
            title: "Width",
            dataIndex: "width",
            width: 90,
            align: "center",
            render: (v) => v || notSet,
        },
        {
            title: "Scale",
            dataIndex: "scale",
            width: 90,
            align: "center",
            render: (v) => v || notSet,
        },
        {
            title: "Status",
            width: 110,
            align: "center",
            render: (_, record) =>
                isConfigured(record) ? (
                    <span className="cc-status cc-status-ready">
                        <CheckCircleFilled /> Ready
                    </span>
                ) : (
                    <span className="cc-status cc-status-pending">
                        <ClockCircleFilled /> Pending
                    </span>
                ),
        },
        {
            title: "",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, record) => (
                <Tooltip title="Edit">
                    <button
                        type="button"
                        className="cc-edit-btn"
                        onClick={() => handleEdit(record)}
                    >
                        <EditOutlined />
                    </button>
                </Tooltip>
            ),
        },
    ];

    const inputStyle = { height: 44, borderRadius: 10, fontSize: 14 };

    return (
        <div className="cc-page">
            {/* ── Page header ───────────────────────────────────────────── */}
            <div className="cc-header">
                <div className="cc-header-left">
                    <div className="cc-header-icon">
                        <SettingOutlined />
                    </div>
                    <div>
                        <h2 className="cc-title">Counter Configuration</h2>
                        <p className="cc-subtitle">
                            Manage systems, stations and printers for each billing counter
                        </p>
                    </div>
                </div>

                <div className="cc-stats">
                    <div className="cc-stat cc-stat-indigo">
                        <span className="cc-stat-num">{totalCount}</span>
                        <span className="cc-stat-label">Counters</span>
                    </div>
                    <div className="cc-stat cc-stat-green">
                        <span className="cc-stat-num">{readyCount}</span>
                        <span className="cc-stat-label">Ready</span>
                    </div>
                    <div className="cc-stat cc-stat-amber">
                        <span className="cc-stat-num">{pendingCount}</span>
                        <span className="cc-stat-label">Pending</span>
                    </div>
                </div>
            </div>

            <Row gutter={[20, 20]} align="top">
                {/* ── Table panel ───────────────────────────────────────── */}
                <Col xs={24} xl={16}>
                    <div className="cc-panel cc-panel-blue">
                        <div className="cc-panel-head">
                            <span className="cc-panel-title cc-title-blue">
                                <span className="cc-title-icon cc-icon-blue">
                                    <UnorderedListOutlined />
                                </span>
                                All Counters
                            </span>
                            <Input
                                allowClear
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search system, station, printer…"
                                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                                className="cc-search"
                            />
                        </div>

                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            loading={loading}
                            size="middle"
                            scroll={{ x: 1100 }}
                            pagination={{
                                pageSize: 10,
                                size: "small",
                                showSizeChanger: false,
                                showTotal: (total) => `${total} counters`,
                            }}
                            rowClassName={(record) =>
                                `cc-row ${record.sno === selectedSno ? "cc-row-selected" : ""}`
                            }
                            onRow={(record) => ({
                                onClick: () => handleEdit(record),
                            })}
                        />
                    </div>
                </Col>

                {/* ── Edit panel ────────────────────────────────────────── */}
                <Col xs={24} xl={8}>
                    <div className="cc-panel cc-panel-purple cc-edit-panel">
                        <div className="cc-panel-head">
                            <span className="cc-panel-title cc-title-purple">
                                <span className="cc-title-icon cc-icon-purple">
                                    <FormOutlined />
                                </span>
                                Update Configuration
                            </span>
                            {selectedSno && (
                                <Tooltip title="Cancel editing">
                                    <button
                                        type="button"
                                        className="cc-close-btn"
                                        onClick={() => form.resetFields()}
                                    >
                                        <CloseOutlined />
                                    </button>
                                </Tooltip>
                            )}
                        </div>

                        {/* Hint (no selection) / Selected summary */}
                        {noSelection ? (
                            <div className="cc-hint">
                                <InfoCircleOutlined className="cc-hint-icon" />
                                <span>
                                    <b>No counter selected.</b> Click a row in the table to enable these fields.
                                </span>
                            </div>
                        ) : (
                            selectedRecord && (
                                <div className="cc-selected">
                                    <div className="cc-selected-avatar">
                                        <DesktopOutlined />
                                    </div>
                                    <div className="cc-selected-info">
                                        <span className="cc-selected-name">
                                            {selectedRecord.sysname || "Unnamed system"}
                                        </span>
                                        <span className="cc-selected-sub">
                                            Counter #{selectedRecord.sno} · {selectedRecord.station || "No station"}
                                        </span>
                                    </div>
                                </div>
                            )
                        )}

                        {/* Form is always visible */}
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            requiredMark={false}
                        >
                            <div className="cc-section-label cc-section-blue">
                                <span className="cc-dot cc-dot-blue" />
                                Counter details
                            </div>

                            <Row gutter={12}>
                                <Col span={9}>
                                    <Form.Item name="sno" label="SNO">
                                        <Input
                                            readOnly
                                            disabled={noSelection}
                                            className="cc-input-locked"
                                            prefix={<NumberOutlined style={{ color: "#eab308", fontSize: 16 }} />}
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={15}>
                                    <Form.Item name="station" label="Station">
                                        <Input
                                            readOnly
                                            disabled={noSelection}
                                            className="cc-input-locked"
                                            ref={stationRef}
                                            onPressEnter={() => branchRef.current?.focus()}
                                            prefix={<ApartmentOutlined style={{ color: "#7c3aed", fontSize: 16 }} />}
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="sysname" label="System Name">
                                <Input
                                    ref={sysnameRef}
                                    disabled={noSelection}
                                    onPressEnter={() => stationRef.current?.focus()}
                                    prefix={<DesktopOutlined style={{ color: "#2563eb", fontSize: 16 }} />}
                                    style={inputStyle}
                                />
                            </Form.Item>

                            <Form.Item name="branchname" label="Branch Name">
                                <Input
                                    ref={branchRef}
                                    disabled={noSelection}
                                    onPressEnter={() => billingRef.current?.focus()}
                                    prefix={<BankOutlined style={{ color: "#16a34a", fontSize: 16 }} />}
                                    style={inputStyle}
                                />
                            </Form.Item>

                            <div className="cc-section-label cc-section-orange">
                                <span className="cc-dot cc-dot-orange" />
                                Printers
                            </div>

                            <Form.Item name="billingprinter" label="Billing Printer">
                                <Input
                                    ref={billingRef}
                                    disabled={noSelection}
                                    onPressEnter={() => estimationRef.current?.focus()}
                                    prefix={<PrinterOutlined style={{ color: "#ea580c", fontSize: 16 }} />}
                                    style={inputStyle}
                                />
                            </Form.Item>

                            <Form.Item name="estimationprinter" label="Estimation Printer">
                                <Input
                                    ref={estimationRef}
                                    disabled={noSelection}
                                    onPressEnter={() => barcodeRef.current?.focus()}
                                    prefix={<PrinterOutlined style={{ color: "#db2777", fontSize: 16 }} />}
                                    style={inputStyle}
                                />
                            </Form.Item>

                            <Form.Item name="barcodeprinter" label="Barcode Printer">
                                <Input
                                    ref={barcodeRef}
                                    disabled={noSelection}
                                    onPressEnter={() => widthRef.current?.focus()}
                                    prefix={<BarcodeOutlined style={{ color: "#dc2626", fontSize: 16 }} />}
                                    style={inputStyle}
                                />
                            </Form.Item>

                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item name="width" label="Width" rules={[NUMERIC_RULE]}>
                                        <Input
                                            ref={widthRef}
                                            disabled={noSelection}
                                            inputMode="decimal"
                                            onPressEnter={() => scaleRef.current?.focus()}
                                            prefix={<ColumnWidthOutlined style={{ color: "#0891b2", fontSize: 16 }} />}
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="scale" label="Scale" rules={[NUMERIC_RULE]}>
                                        <Input
                                            ref={scaleRef}
                                            disabled={noSelection}
                                            inputMode="decimal"
                                            onPressEnter={() => updateBtnRef.current?.focus()}
                                            prefix={<ZoomInOutlined style={{ color: "#9333ea", fontSize: 16 }} />}
                                            style={inputStyle}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <div className="cc-actions">
                                <Button
                                    className="cc-cancel-btn"
                                    disabled={noSelection}
                                    onClick={() => form.resetFields()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    ref={updateBtnRef}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={noSelection}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            form.submit();
                                        }
                                    }}
                                    icon={<SaveOutlined />}
                                    className="cc-update-btn"
                                >
                                    Update Record
                                </Button>
                            </div>
                        </Form>
                    </div>
                </Col>
            </Row>

            <style>
                {`
/* ── Page ─────────────────────────────────────────── */
.cc-page {
    padding: 22px;
    background: linear-gradient(135deg, #eef2ff 0%, #f8fafc 50%, #f1f5f9 100%);
}

/* ── Header ───────────────────────────────────────── */
.cc-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;
}
.cc-header-left { display: flex; align-items: center; gap: 14px; }
.cc-header-icon {
    width: 50px; height: 50px;
    border-radius: 15px;
    display: flex; align-items: center; justify-content: center;
    font-size: 23px; color: #fff;
    background: linear-gradient(135deg, #4f46e5, #7c3aed 60%, #db2777);
    box-shadow: 0 8px 20px rgba(79,70,229,0.35);
}
.cc-title {
    margin: 0; font-size: 23px; font-weight: 800; line-height: 1.2;
    background: linear-gradient(90deg, #4338ca 0%, #7c3aed 55%, #db2777 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: #4338ca;
}
.cc-subtitle { margin: 2px 0 0; font-size: 13px; color: #64748b; }

.cc-stats { display: flex; gap: 10px; }
.cc-stat {
    min-width: 92px;
    padding: 8px 16px;
    background: #fff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    border-top: 3px solid #6366f1;
    display: flex; flex-direction: column; align-items: center;
    box-shadow: 0 4px 14px rgba(15,23,42,0.05);
}
.cc-stat-num { font-size: 20px; font-weight: 800; color: #1e293b; line-height: 1.2; }
.cc-stat-label { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #94a3b8; }
.cc-stat-indigo { border-top-color: #6366f1; }
.cc-stat-indigo .cc-stat-num { color: #4f46e5; }
.cc-stat-green { border-top-color: #10b981; }
.cc-stat-green .cc-stat-num { color: #059669; }
.cc-stat-amber { border-top-color: #f59e0b; }
.cc-stat-amber .cc-stat-num { color: #d97706; }

/* ── Panels ───────────────────────────────────────── */
.cc-panel {
    background: #fff;
    border-radius: 18px;
    border: 1px solid #e8edf5;
    border-top: 4px solid #6366f1;
    box-shadow: 0 10px 30px rgba(15,23,42,0.06);
    padding: 18px 18px 14px;
}
.cc-panel-blue { border-top-color: #2563eb; }
.cc-panel-purple { border-top-color: #7c3aed; }

.cc-panel-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; margin-bottom: 14px;
}
.cc-panel-title {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 17px; font-weight: 800;
}
.cc-title-blue {
    background: linear-gradient(90deg, #1d4ed8, #0ea5e9);
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent; color: #1d4ed8;
}
.cc-title-purple {
    background: linear-gradient(90deg, #6d28d9, #db2777);
    -webkit-background-clip: text; background-clip: text;
    -webkit-text-fill-color: transparent; color: #6d28d9;
}
.cc-title-icon {
    width: 32px; height: 32px; border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 15px; color: #fff;
    -webkit-text-fill-color: #fff;
}
.cc-icon-blue { background: linear-gradient(135deg, #2563eb, #0ea5e9); box-shadow: 0 6px 14px rgba(37,99,235,0.3); }
.cc-icon-purple { background: linear-gradient(135deg, #7c3aed, #db2777); box-shadow: 0 6px 14px rgba(124,58,237,0.3); }

.cc-search { max-width: 280px; height: 40px; border-radius: 10px; }

/* ── Table ────────────────────────────────────────── */
.cc-page .ant-table { background: transparent; }
.cc-page .ant-table-thead > tr > th {
    background: linear-gradient(135deg, #e0e7ff, #ede9fe) !important;
    color: #3730a3 !important;
    font-weight: 800 !important;
    font-size: 12px !important;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #c7d2fe !important;
    white-space: nowrap;
}
.cc-page .ant-table-thead > tr > th::before { display: none !important; }
.cc-page .ant-table-tbody > tr > td {
    border-bottom: 1px solid #f1f5f9;
    font-size: 13.5px;
    color: #1e293b;
}
.cc-row { cursor: pointer; transition: background 0.2s ease; }
.cc-page .ant-table-tbody > tr.cc-row:hover > td { background: #f1f5ff !important; }
.cc-page .ant-table-tbody > tr.cc-row-selected > td {
    background: #e0e7ff !important;
}
.cc-page .ant-table-tbody > tr.cc-row-selected > td:first-child {
    box-shadow: inset 3px 0 0 #4f46e5;
}

.cc-sno {
    display: inline-block; min-width: 28px; padding: 2px 8px;
    border-radius: 8px; background: #eef2ff;
    font-weight: 700; font-size: 12px; color: #4f46e5;
}
.cc-sys { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; }
.cc-sys-icon { color: #2563eb; }
.cc-station-tag {
    margin: 0; border: none; border-radius: 8px;
    background: #ede9fe; color: #6d28d9; font-weight: 600;
}
.cc-muted { color: #cbd5e1; font-style: italic; font-size: 12.5px; }

.cc-status {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 999px;
    font-size: 12px; font-weight: 600;
}
.cc-status-ready { background: #d1fae5; color: #047857; }
.cc-status-pending { background: #fef3c7; color: #b45309; }

.cc-edit-btn {
    width: 34px; height: 34px;
    border: none; border-radius: 10px; cursor: pointer;
    display: inline-flex; align-items: center; justify-content: center;
    color: #4f46e5; background: #eef2ff;
    transition: all 0.2s ease;
}
.cc-edit-btn:hover { background: #4f46e5; color: #fff; transform: translateY(-1px); }

/* ── Edit panel ───────────────────────────────────── */
.cc-edit-panel { position: sticky; top: 20px; min-width: 0; }
.cc-close-btn {
    width: 30px; height: 30px; border: none; border-radius: 8px; cursor: pointer;
    background: #f1f5f9; color: #64748b;
    display: inline-flex; align-items: center; justify-content: center;
    transition: all 0.2s ease;
}
.cc-close-btn:hover { background: #fee2e2; color: #dc2626; }

/* hint shown when nothing is selected */
.cc-hint {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 14px; margin-bottom: 4px;
    border-radius: 12px;
    background: #fffbeb; border: 1px dashed #fcd34d;
    font-size: 12.5px; color: #92400e;
}
.cc-hint b { color: #78350f; }
.cc-hint-icon { font-size: 16px; color: #f59e0b; }

.cc-selected {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; margin-bottom: 4px;
    border-radius: 14px;
    background: linear-gradient(135deg, #eef2ff, #f5f3ff);
    border: 1px solid #e0e7ff;
}
.cc-selected-avatar {
    width: 40px; height: 40px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 18px;
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
}
.cc-selected-info { display: flex; flex-direction: column; min-width: 0; }
.cc-selected-name { font-weight: 700; font-size: 14.5px; color: #1e1b4b; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cc-selected-sub { font-size: 12px; color: #6366f1; }

/* coloured section labels */
.cc-section-label {
    display: flex; align-items: center; gap: 8px;
    margin: 14px 0 10px; padding: 6px 10px;
    border-radius: 8px;
    font-size: 11.5px; font-weight: 800; letter-spacing: 0.08em;
    text-transform: uppercase;
}
.cc-section-blue { background: #eff6ff; color: #1d4ed8; }
.cc-section-orange { background: #fff7ed; color: #c2410c; }
.cc-dot { width: 8px; height: 8px; border-radius: 50%; }
.cc-dot-blue { background: #2563eb; }
.cc-dot-orange { background: #ea580c; }

.cc-page .ant-form-item { margin-bottom: 14px; }
.cc-page .ant-form-item-label > label { font-size: 13px; font-weight: 600; color: #334155; }
.cc-page .ant-input-affix-wrapper {
    width: 100%;
    border-color: #e2e8f0;
    transition: all 0.2s ease;
}
.cc-page .ant-input-affix-wrapper:hover { border-color: #a5b4fc; }
.cc-page .ant-input-affix-wrapper-focused,
.cc-page .ant-input-affix-wrapper:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.15);
}
.cc-page .ant-input-affix-wrapper .ant-input-prefix { margin-right: 10px; }
.cc-page .cc-input-locked,
.cc-page .cc-input-locked input { background: #f8fafc !important; color: #64748b; cursor: not-allowed; }

/* disabled (no selection) look */
.cc-page .ant-input-affix-wrapper-disabled,
.cc-page .ant-input-affix-wrapper-disabled input {
    background: #f8fafc !important;
    color: #94a3b8 !important;
    cursor: not-allowed;
}
.cc-page .ant-input-affix-wrapper-disabled .anticon { opacity: 0.55; }

.cc-actions { display: flex; gap: 10px; margin-top: 6px; }
.cc-cancel-btn {
    height: 46px; border-radius: 12px; font-weight: 600;
    border: 1px solid #e2e8f0; color: #475569; background: #fff;
    flex: 0 0 96px;
}
.cc-cancel-btn:hover:not(:disabled) { border-color: #cbd5e1 !important; color: #1e293b !important; background: #f8fafc !important; }
.cc-update-btn {
    flex: 1; height: 46px; border-radius: 12px;
    font-size: 15px; font-weight: 700;
    border: none !important; color: #fff !important;
    background: linear-gradient(135deg, #10b981, #059669) !important;
    box-shadow: 0 8px 20px rgba(16,185,129,0.3);
    transition: all 0.25s ease;
}
.cc-update-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 26px rgba(16,185,129,0.4) !important;
}
.cc-update-btn:active:not(:disabled) { transform: scale(0.98); }
.cc-update-btn:disabled {
    background: #cbd5e1 !important;
    box-shadow: none;
    color: #fff !important;
    opacity: 0.8;
}

@media (max-width: 1199px) {
    .cc-edit-panel { position: static; }
    .cc-search { max-width: 100%; }
}
`}
            </style>
        </div>
    );
};

export default CounterConfigMaster;